import type { TFunction } from 'i18next'
import { ListingMetaPayload } from 'newTypes/unit'
import { tListingTypeLabel, tRoomCountLabel, tUnitTypeLabel } from 'utils/i18nLocale'

type ListingCardSource = {
  bedrooms?: unknown
  listingMeta?: ListingMetaPayload | null
  listing_meta?: ListingMetaPayload | null
  unitType?: unknown
}

function normalizeRoomKey(value: unknown): string | null {
  if (value == null || value === '') return null
  const raw = String(value)
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(',', '.')
    .replace('½', '.5')
    .replace(/1\/2/g, '.5')
  if (raw.includes('studio')) return 'studio'
  if (raw === '6.5+' || raw.startsWith('6.5')) return '6.5+'
  if (raw === '5.5+') return '5.5+'
  if (raw === '5.5') return '5.5'
  if (raw === '2.5' || raw === '3.5' || raw === '4.5') return raw
  const n = Number(raw)
  if (!Number.isNaN(n)) {
    if (n >= 6) return '6.5+'
    if (n === 5) return '5.5'
    if (Number.isInteger(n) && n >= 1) return String(n)
  }
  return raw || null
}

export function listingRoomLabel(t: TFunction, bedrooms: unknown): string {
  const key = normalizeRoomKey(bedrooms)
  if (!key) return '—'
  const translated = tRoomCountLabel(t, key)
  if (translated !== key) return translated
  if (/^\d+$/.test(key)) return `${key}½`
  return key
}

export function listingUnitTypeKey(property: ListingCardSource): string | null {
  const meta = property.listingMeta ?? property.listing_meta
  const fromMeta =
    meta && typeof meta === 'object' && typeof meta.unitType === 'string'
      ? meta.unitType.trim()
      : ''
  const direct =
    typeof property.unitType === 'string' ? property.unitType.trim() : ''
  const raw = fromMeta || direct
  return raw ? raw.toLowerCase() : null
}

export function listingUnitTypeLabel(t: TFunction, property: ListingCardSource): string | null {
  const key = listingUnitTypeKey(property)
  if (!key) return null
  return tUnitTypeLabel(t, key)
}

export function listingTypeLabel(t: TFunction, property: ListingCardSource): string {
  const meta = property.listingMeta ?? property.listing_meta
  const listingType =
    meta && typeof meta === 'object' && typeof meta.listingType === 'string'
      ? meta.listingType.trim()
      : ''
  if (listingType) return tListingTypeLabel(t, listingType)
  return t('propertyCard.leaseShort')
}

export function listingTypeKey(property: ListingCardSource): string {
  const meta = property.listingMeta ?? property.listing_meta
  const listingType =
    meta && typeof meta === 'object' && typeof meta.listingType === 'string'
      ? meta.listingType.trim()
      : ''
  return listingType || 'lease_transfer'
}

export function listingTypeIcon(property: ListingCardSource): string {
  switch (listingTypeKey(property)) {
    case 'sublease':
      return 'regular-house-person-leave'
    case 'for_rent':
      return 'regular-key'
    case 'lease_transfer':
    default:
      return 'regular-file-contract'
  }
}
